import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, Paper, TableHead,
  TableRow, TableSortLabel, Box, Typography, LinearProgress,
} from 'components/mui';
import { Checkbox } from '@mui/material';
import { useCaseList } from 'containers/pages/cases/hooks';
import { getComparator, stableSort } from 'libs/sortList';
import { Case } from 'containers/pages/cases/types';
import CaseListRow from './CaseListRow';
import CaseListEmpty from './CaseListEmpty';
import { INITIAL_VISIBLE_COUNT, LOAD_MORE_COUNT, SCROLL_THRESHOLD } from 'containers/pages/cases/constants';

interface CaseListProps {
  cases: Case[];
  onCaseClick?: (caseId: number) => void;
  selectedIds?: Set<number>;
  onToggleSelect?: (id: number) => void;
  onToggleSelectAll?: () => void;
  onEditCase?: (caseId: number) => void;
  onDuplicateCase?: (caseId: number) => void;
}

function CaseList({ cases, onCaseClick, selectedIds, onToggleSelect, onToggleSelectAll, onEditCase, onDuplicateCase }: CaseListProps) {
  const { state, handler } = useCaseList();
  const listRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sortedCases = useMemo(() => stableSort(cases, getComparator(state.order, state.orderBy) as any) as Case[], [cases, state.order, state.orderBy]);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  useEffect(() => { setVisibleCount(INITIAL_VISIBLE_COUNT); }, [sortedCases, state.order, state.orderBy]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    let sc: HTMLElement | null = el.parentElement;
    while (sc) {
      const s = getComputedStyle(sc);
      if (['auto', 'scroll'].includes(s.overflow) || ['auto', 'scroll'].includes(s.overflowY)) break;
      sc = sc.parentElement;
    }
    if (!sc) return;
    const onScroll = () => {
      const near = sc!.scrollHeight - sc!.scrollTop - sc!.clientHeight < SCROLL_THRESHOLD;
      if (near && !isLoadingMore && visibleCount < sortedCases.length) {
        setIsLoadingMore(true);
        setTimeout(() => { setVisibleCount((p) => Math.min(p + LOAD_MORE_COUNT, sortedCases.length)); setIsLoadingMore(false); }, 100);
      }
    };
    sc.addEventListener('scroll', onScroll, { passive: true });
    return () => sc!.removeEventListener('scroll', onScroll);
  }, [isLoadingMore, visibleCount, sortedCases.length]);

  const visibleRows = useMemo(() => sortedCases.slice(0, visibleCount), [sortedCases, visibleCount]);
  const hasSelection = !!onToggleSelect;
  const totalCols = state.headTableCells.length + (hasSelection ? 1 : 0) + 1;
  const hideOnMobile = new Set(['ConsultantName']);
  const hideOnTablet = new Set(['UpdatedOn']);

  const headSx = { bgcolor: 'background.default', fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.03em', textTransform: 'uppercase', color: 'text.secondary', py: 1 };

  return (
    <Paper ref={listRef} variant="outlined" sx={{ overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, borderBottom: 1, borderColor: 'divider', bgcolor: 'action.hover' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
          {sortedCases.length === 0 ? 'No cases' : `${visibleRows.length} of ${sortedCases.length} cases`}
        </Typography>
      </Box>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': headSx }}>
              {hasSelection && (
                <TableCell padding="checkbox" sx={{ width: 42 }}>
                  <Checkbox size="small"
                    indeterminate={!!selectedIds && selectedIds.size > 0 && selectedIds.size < sortedCases.length}
                    checked={sortedCases.length > 0 && !!selectedIds && selectedIds.size === sortedCases.length}
                    onChange={onToggleSelectAll}
                  />
                </TableCell>
              )}
              {state.headTableCells.map((cell) => {
                const active = state.orderBy === cell.property;
                const rSx = hideOnMobile.has(cell.property) ? { display: { xs: 'none', sm: 'table-cell' } }
                  : hideOnTablet.has(cell.property) ? { display: { xs: 'none', md: 'table-cell' } } : {};
                const wSx = cell.property === '' ? { width: 40 } : cell.property === 'StatusTitle' ? { width: 120 } : cell.property === 'UpdatedOn' ? { width: 100 } : {};
                return (
                  <TableCell key={cell.id} sortDirection={active ? state.order : false} sx={{ ...rSx, ...wSx }}>
                    {cell.property ? <TableSortLabel active={active} direction={active ? state.order : 'asc'} onClick={handler.onSort(cell.property)}>{cell.label}</TableSortLabel> : null}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.length === 0 && (
              <TableRow><TableCell colSpan={totalCols} sx={{ p: 0, border: 0 }}><CaseListEmpty /></TableCell></TableRow>
            )}
            {visibleRows.map((c) => (
              <CaseListRow key={c.Id} caseItem={c} isOpen={state.selectedIds.includes(c.Id)}
                onToggle={() => handler.onToggleExpand(c.Id)} onCaseClick={onCaseClick} totalCols={totalCols}
                selected={selectedIds?.has(c.Id)} onSelect={onToggleSelect} onEdit={onEditCase} onDuplicate={onDuplicateCase}
              />
            ))}
            {isLoadingMore && (
              <TableRow><TableCell colSpan={totalCols} sx={{ p: 0, border: 0 }}><LinearProgress /></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!isLoadingMore && visibleRows.length >= sortedCases.length && sortedCases.length > 0 && (
        <Box sx={{ py: 0.75, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.disabled">End of list</Typography>
        </Box>
      )}
    </Paper>
  );
}

export default CaseList;
